import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { runAuthDiagnostic, testEmailSignup, testOAuthSignin, checkSupabaseProjectSettings, AuthDiagnosticResult } from '@/utils/authDiagnostic';
import { 
  CheckCircle, 
  XCircle, 
  AlertCircle, 
  RefreshCw, 
  Mail, 
  Database,
  Users,
  Settings,
  ExternalLink
} from 'lucide-react';

const AuthDiagnostic = () => {
  const [diagnosticResult, setDiagnosticResult] = useState<AuthDiagnosticResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [testEmail, setTestEmail] = useState('test@example.com');
  const [testPassword, setTestPassword] = useState('TestPassword123!');
  const { toast } = useToast();

  const runDiagnostic = useCallback(async () => {
    setLoading(true);
    try {
      const result = await runAuthDiagnostic();
      setDiagnosticResult(result);
      
      toast({
        title: 'Diagnostic Complete',
        description: `Found ${result.errors.length} issues`,
        variant: result.errors.length > 0 ? 'destructive' : 'default'
      });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Diagnostic failed unexpectedly';
      toast({
        title: 'Diagnostic Failed',
        description: message,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const testSignup = async () => {
    setLoading(true);
    try {
      const result = await testEmailSignup(testEmail, testPassword);
      toast({
        title: result.success ? 'Signup Test Successful' : 'Signup Test Failed',
        description: result.success ? 'Check your email for confirmation' : result.error,
        variant: result.success ? 'default' : 'destructive'
      });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Signup test encountered an unknown error';
      toast({
        title: 'Signup Test Error',
        description: message,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const testGoogleOAuth = async () => {
    setLoading(true);
    try {
      const result = await testOAuthSignin('google');
      if (result.success) {
        toast({
          title: 'OAuth Test Initiated',
          description: 'Redirecting to Google OAuth...'
        });
      } else {
        toast({
          title: 'OAuth Test Failed',
          description: result.error,
          variant: 'destructive'
        });
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'OAuth test encountered an unknown error';
      toast({
        title: 'OAuth Test Error',
        description: message,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void runDiagnostic();
  }, [runDiagnostic]);

  const projectSettings = checkSupabaseProjectSettings();

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Authentication Diagnostic</h1>
            <p className="text-muted-foreground">Check OAuth flow and Supabase authentication setup</p>
          </div>
          <Button onClick={runDiagnostic} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Run Diagnostic
          </Button>
        </div>

        {/* Project Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Supabase Project Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Project ID</Label>
                <div className="font-mono text-sm bg-muted p-2 rounded">{projectSettings.projectId}</div>
              </div>
              <div>
                <Label>Supabase URL</Label>
                <div className="font-mono text-sm bg-muted p-2 rounded">{projectSettings.supabaseUrl}</div>
              </div>
              <div>
                <Label>Current Origin</Label>
                <div className="font-mono text-sm bg-muted p-2 rounded">{projectSettings.currentOrigin}</div>
              </div>
              <div>
                <Label>Current Host</Label>
                <div className="font-mono text-sm bg-muted p-2 rounded">{projectSettings.currentHost}</div>
              </div>
            </div>
            <div className="flex gap-2">
              <Badge variant={projectSettings.hasAnonKey ? 'default' : 'destructive'}>
                {projectSettings.hasAnonKey ? 'Anon Key' : 'Missing Anon Key'}
              </Badge>
              <Badge variant={projectSettings.hasServiceKey ? 'default' : 'destructive'}>
                {projectSettings.hasServiceKey ? 'Service Key' : 'Missing Service Key'}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Diagnostic Results */}
        {diagnosticResult && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="w-5 h-5" />
                Diagnostic Results
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  {diagnosticResult.apiHealthy ? (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-500" />
                  )}
                  <span>Auth API Health</span>
                </div>
                <div className="flex items-center gap-2">
                  {diagnosticResult.supabaseConnection ? (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-500" />
                  )}
                  <span>Supabase Connection</span>
                </div>
                <div className="flex items-center gap-2">
                  {diagnosticResult.authTablesExist === true ? (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  ) : diagnosticResult.authTablesExist === false ? (
                    <XCircle className="w-5 h-5 text-red-500" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-yellow-500" />
                  )}
                  <span>Auth Tables Accessible</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-500" />
                  <span>Auth Users Count: {diagnosticResult.authUsersCount ?? 'N/A'}</span>
                </div>
                <div className="flex items-center gap-2">
                  {diagnosticResult.currentUser ? (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-yellow-500" />
                  )}
                  <span>
                    Current User: {diagnosticResult.currentUser ? diagnosticResult.currentUser.email ?? 'Unknown Email' : 'Not logged in'}
                  </span>
                </div>
              </div>

              {diagnosticResult.authSettings && (
                <div className="bg-muted p-4 rounded-lg">
                  <h4 className="font-semibold mb-2">Session Information</h4>
                  <div className="space-y-1 text-sm">
                    <div>Has Session: {diagnosticResult.authSettings.hasSession ? 'Yes' : 'No'}</div>
                    <div>User Email: {diagnosticResult.authSettings.userEmail || 'N/A'}</div>
                    <div>Email Confirmed: {diagnosticResult.authSettings.userConfirmedAt ? 'Yes' : 'No'}</div>
                    <div>Session Expiry: {diagnosticResult.authSettings.sessionExpiry ?? 'N/A'}</div>
                  </div>
                </div>
              )}

              {diagnosticResult.errors.length > 0 && (
                <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                  <h4 className="font-semibold text-red-800 mb-2">Errors Found</h4>
                  <ul className="space-y-1 text-sm text-red-700">
                    {diagnosticResult.errors.map((error, index) => (
                      <li key={index}>• {error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Test Functions */}
        <Card>
          <CardHeader>
            <CardTitle>Test Authentication Functions</CardTitle>
            <CardDescription>Test signup and OAuth flows</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Test Email</Label>
                <Input
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="test@example.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Test Password</Label>
                <Input
                  type="password"
                  value={testPassword}
                  onChange={(e) => setTestPassword(e.target.value)}
                  placeholder="TestPassword123!"
                />
              </div>
            </div>
            
            <div className="flex gap-2 flex-wrap">
              <Button onClick={testSignup} disabled={loading}>
                <Mail className="w-4 h-4 mr-2" />
                Test Email Signup
              </Button>
              <Button onClick={testGoogleOAuth} disabled={loading}>
                <ExternalLink className="w-4 h-4 mr-2" />
                Test Google OAuth
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Recommendations */}
        <Card>
          <CardHeader>
            <CardTitle>Common Issues & Solutions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-semibold text-blue-800 mb-2">Email Confirmation Issues</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Check Supabase Dashboard → Authentication → Settings → Email Templates</li>
                  <li>• Verify SMTP settings are configured</li>
                  <li>• Check spam folder for confirmation emails</li>
                  <li>• Ensure Site URL is set correctly in Auth settings</li>
                </ul>
              </div>
              
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <h4 className="font-semibold text-yellow-800 mb-2">OAuth Configuration</h4>
                <ul className="text-sm text-yellow-700 space-y-1">
                  <li>• Check OAuth provider settings in Supabase Dashboard</li>
                  <li>• Verify redirect URIs match your domain</li>
                  <li>• Ensure client IDs and secrets are correct</li>
                  <li>• Check if OAuth providers are enabled</li>
                </ul>
              </div>
              
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <h4 className="font-semibold text-green-800 mb-2">Database Tables</h4>
                <ul className="text-sm text-green-700 space-y-1">
                  <li>• auth.users table is managed by Supabase (not visible in main dashboard)</li>
                  <li>• Use SQL Editor to query: SELECT * FROM auth.users;</li>
                  <li>• Check if migrations have been applied</li>
                  <li>• Verify RLS policies are correctly set up</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AuthDiagnostic;
